import {Injectable} from '@angular/core';
import {LoggerService} from '../../../logging/services/logger.service';
import {LogLevel} from '../../../logging/data/logLevel';
import {LogSourceType} from '../../../logging/data/logSourceType';
import {LogSource} from '../../../logging/data/logSource';
import {GlobalSettingsService, OrgSettingsKeys} from '../global-settings.service';

@Injectable({
  providedIn: 'root'
})
export class ColorService {

  currentTheme = 0;
  isDarkTheme: boolean;
  private themeWrapper = document.querySelector('body');
  // private themes = [['A9A9A9FF', 'A9A9A9FF', '000000', '222222', '440000'], ['000000', '1982c4', 'FFFFFF', 'B6F2FF55', 'FFDED7FF']];
  private darkTheme = {
    '--ui-gradient-user-0': 'rgb(214, 23, 23)',
    '--ui-gradient-user-1': 'rgb(214, 90, 23)',
    '--ui-color-content': 'rgb(236,236,236)',
    '--ui-color-main': '#63b4ee',
    '--ui-color-on-main': 'rgb(255, 255, 255)',
    '--ui-gradient-bg-0': 'rgba(0,0,0,1)',
    '--ui-gradient-bg-1': 'rgba(0,0,0,1)',
    '--ui-gradient-main-0': '#63b4ee',
    '--ui-gradient-main-1': '#63b4ee',
    '--ui-gradient-container-0': 'rgba(207, 207, 207, 0.1)',
    '--ui-gradient-container-1': 'rgba(76, 76, 76, 0.1)',
    '--ui-card-color-0': 'rgb(66,66,66)',
    '--ui-card-color-1': 'rgba(66,66,66,1)',
    '--ui-color-shadow': 'rgba(255,255,255,0.14)',
    '--ui-color-secondary': 'rgba(255,255,255,0.05)',
    '--ui-color-search': 'rgb(255,255,255)',
    '--ui-bg-selection': 'rgba(20, 48, 186, 0.2)',
    '--ck-color-toolbar-background': 'rgba(12,18,47,1)',
    '--ck-color-toolbar-border': 'rgb(9,13,36)',
    '--ck-color-text': 'rgb(191,191,191)',
    '--ck-color-button-on-background': 'rgb(9,13,36)',
    '--ui-avatar-background': 'rgb(220,220,220)',
    '--ui-icon-background': 'rgba(255,255,255,0.25)',
    '--ui-main-menu-reversed': 'rgba(64,64,64,0.85)',
    '--ui-menu-tabs-unselected': 'rgba(255,255,255,1)',
    '--ui-input-background': 'rgb(40,40,40)',
    '--ui-input-text': '#63b4ee',
  };
  private shiftedDarkTheme = {
    '--ui-gradient-user-0': 'rgb(214, 23, 23)',
    '--ui-gradient-user-1': 'rgb(214, 90, 23)',
    '--ui-color-content': 'rgb(207, 207, 207)',
    '--ui-color-main': 'rgb(20,176,203)',
    '--ui-color-on-main': 'rgb(255, 255, 255)',
    '--ui-gradient-bg-0': 'rgba(45,12,19,1)',
    '--ui-gradient-bg-1': 'rgba(12,18,47,1)',
    '--ui-gradient-main-0': 'rgb(112,112,112)',
    '--ui-gradient-main-1': 'rgb(105,105,105)',
    '--ui-gradient-container-0': 'rgba(207, 207, 207, 0.1)',
    '--ui-gradient-container-1': 'rgba(76, 76, 76, 0.1)',
    '--ui-card-color-0': 'rgba(45,12,19,1)',
    '--ui-card-color-1': 'rgba(12,18,47,1)',
    '--ui-color-shadow': 'rgba(255,255,255,0.14)',
    '--ui-color-secondary': 'rgba(255,255,255,0.05)',
    '--ui-color-search': 'rgb(255,255,255)',
    '--ui-bg-selection': 'rgba(136,20,186,0.2)',
    '--ck-color-toolbar-background': 'rgba(12,18,47,1)',
    '--ck-color-toolbar-border': 'rgb(9,13,36)',
    '--ck-color-text': 'rgb(191,191,191)',
    '--ck-color-button-on-background': 'rgb(9,13,36)',
    '--ui-avatar-background': 'rgb(220,220,220)',
    '--ui-icon-background': 'rgba(255,255,255,0.25)',
    '--ui-main-menu-reversed': 'rgba(64,64,64,0.85)',
    '--ui-menu-tabs-unselected': 'rgba(102,102,102,1)',
    '--ui-input-background': 'rgb(40,40,40)',
    '--ui-input-text': 'rgba(0,83,166,1)',
  };
  private lightTheme = {
    // the variables should be kept sorted alphabetically
    '--ck-color-button-on-background': 'rgb(203,203,203)',
    '--ck-color-toolbar-background': 'rgb(236,236,236)',
    '--ck-color-toolbar-border': 'rgb(203,203,203)',
    '--ck-color-text': '#002940',
    '--ui-bg-selection': 'rgba(20, 48, 186,0.2)',
    '--ui-card-color-0': 'rgb(255,255,255)',
    '--ui-card-color-1': 'rgb(255,255,255)',
    '--ui-color-content': 'rgb(22, 22, 22)',
    '--ui-color-main': '#008aed',
    '--ui-color-on-main': 'rgb(255, 255, 255)',
    '--ui-color-shadow': 'rgba(0,0,0,0.25)',
    '--ui-color-secondary': 'rgb(242, 242, 242)',
    '--ui-color-search': 'rgb(0,0,0)',
    '--ui-color-urgent': 'rgba(255,47,0,0.15)',
    '--ui-gradient-bg-0': 'rgb(255,255,255)',
    '--ui-gradient-bg-1': 'rgb(255,255,255)',
    '--ui-gradient-user-0': 'rgb(214, 23, 23)',
    '--ui-gradient-user-1': 'rgb(214, 90, 23)',
    '--ui-gradient-container-0': 'rgba(255,255,255,0.25)',
    '--ui-gradient-container-1': 'rgba(255,255,255,0)',
    '--ui-gradient-main-0': '#008aed',
    '--ui-gradient-main-1': '#008aed',
    '--ui-icon-background': 'rgba(255,255,255,0.25)',
    '--ui-main-menu-reversed': 'rgba(64,64,64,0.85)',
    '--ui-menu-tabs-unselected': 'rgba(102,102,102,1)',
    '--ui-input-background': 'rgba(242,242,242,1)',
    '--ui-input-text': 'rgba(0,83,166,1)',
    '--ui-avatar-background': 'rgb(220,220,220)',
    '--ck-color-mention-module-name-text': 'var(--ui-gradient-main-0)',
    '--ck-color-mention-module-name-background': 'rgb(0 138 237 / 10%)',
  };

  private shiftedLightTheme = {
    '--ui-gradient-user-0': 'rgb(214, 23, 23)',
    '--ui-gradient-user-1': 'rgb(214, 90, 23)',
    '--ui-color-content': 'rgb(22, 22, 22)',
    '--ui-color-main': 'rgb(20,176,203)',
    '--ui-color-on-main': 'rgb(255, 255, 255)',
    '--ui-gradient-bg-0': 'rgb(151, 188, 193)',
    '--ui-gradient-bg-1': 'rgb(252, 252, 252)',
    '--ui-gradient-main-0': 'rgb(20,176,203)',
    '--ui-gradient-main-1': 'rgb(136,20,186)',
    '--ui-gradient-container-0': 'rgba(162,162,162,0.1)',
    '--ui-gradient-container-1': 'rgba(0,0,0,0.2)',
    '--ui-card-color-0': 'rgb(255,255,255)',
    '--ui-card-color-1': 'rgb(255,255,255)',
    '--ui-color-shadow': 'rgba(0,0,0,0.14)',
    '--ui-color-secondary': 'rgba(0,0,167,0.05)',
    '--ui-color-search': 'rgb(0,0,0)',
    '--ui-bg-selection': 'rgba(136,20,186,0.2)',
    '--ck-color-toolbar-background': 'rgb(236,236,236)',
    '--ck-color-toolbar-border': 'rgb(203,203,203)',
    '--ck-color-text': 'rgb(45,45,45)',
    '--ck-color-button-on-background': 'rgb(203,203,203)',
    '--ui-avatar-background': 'rgb(220,220,220)',
  };
  private themes = [];

  constructor(private loggerService: LoggerService,
              private globalSettingsService: GlobalSettingsService) {
    this.themes.push(this.lightTheme);
    this.themes.push(this.darkTheme);
    this.addExtraVariables();
    this.init();
  }

  addExtraVariables() {
    for (const theme of this.themes) {
      theme['--ui-card-padding'] = '10px';
      theme['--ui-box-shadow-01'] = '0 0 8px var(--ui-color-shadow)';
    }
  }

  async init() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.ColorService, 'Initializing color service');
    const str = await localStorage.getItem('app-theme');
    if (!!str) {
      if (!isNaN(Number(str)) && Number(str) >= 0 && Number(str) < this.themes.length) {
        this.currentTheme = Number(str);
        this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.ColorService, 'Loaded theme from storage');
        await this.changeTheme(this.themes[Number(str)]);
      } else {
        this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.ColorService, 'Invalid theme in storage, loading default');
        await this.changeTheme(this.themes[0]);
      }
    } else {
      await this.changeTheme(this.themes[0]);
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.ColorService, 'No theme in storage, loading default');
    }
    this.applyOverridesFromLocalStorage();
    await this.applyOverridesFromServer();
  }

  applyOverridesFromLocalStorage() {
    const enabled = localStorage.getItem('customization-accent-enabled');
    if (enabled !== 'true') {
      return;
    }
    const accentColor = localStorage.getItem('customization-accent-color');
    const accentGradient0 = localStorage.getItem('customization-accent-gradient-0');
    const accentGradient1 = localStorage.getItem('customization-accent-gradient-1');
    this.themeWrapper.style.setProperty('--ui-color-main', accentColor);
    this.themeWrapper.style.setProperty('--ui-gradient-main-0', accentGradient0);
    this.themeWrapper.style.setProperty('--ui-gradient-main-1', accentGradient1);
  }

  async applyOverridesFromServer() {
    const enabled = await this.globalSettingsService.getSetting(OrgSettingsKeys.CUST_ACCENT_ENABLED);
    localStorage.setItem('customization-accent-enabled', enabled);
    if (enabled !== 'true') {
      this.applyDefaultThemeAccent();
      return;
    }
    const accentColor = await this.globalSettingsService.getSetting(OrgSettingsKeys.CUST_ACCENT_COLOR);
    const accentGradient0 = await this.globalSettingsService.getSetting(OrgSettingsKeys.CUST_ACCENT_GRADIENT_0);
    const accentGradient1 = await this.globalSettingsService.getSetting(OrgSettingsKeys.CUST_ACCENT_GRADIENT_1);
    this.themeWrapper.style.setProperty('--ui-color-main', accentColor);
    this.themeWrapper.style.setProperty('--ui-gradient-main-0', accentGradient0);
    this.themeWrapper.style.setProperty('--ui-gradient-main-1', accentGradient1);
    localStorage.setItem('customization-accent-color', accentColor);
    localStorage.setItem('customization-accent-gradient-0', accentGradient0);
    localStorage.setItem('customization-accent-gradient-1', accentGradient1);
  }

  applyDefaultThemeAccent() {
    const currentTheme = this.themes[this.currentTheme];
    this.themeWrapper.style.setProperty('--ui-color-main', currentTheme['--ui-color-main']);
    this.themeWrapper.style.setProperty('--ui-gradient-main-0', currentTheme['--ui-gradient-main-0']);
    this.themeWrapper.style.setProperty('--ui-gradient-main-1', currentTheme['--ui-gradient-main-1']);
    localStorage.removeItem('customization-accent-enabled');
    localStorage.removeItem('customization-accent-color');
    localStorage.removeItem('customization-accent-gradient-0');
    localStorage.removeItem('customization-accent-gradient-1');
  }


  // tslint:disable-next-line:no-unnecessary-initializer
  async changeTheme(theme: {} = undefined) {
    if (theme === undefined) {
      this.currentTheme += 1;
      if (this.currentTheme > this.themes.length) {
        this.currentTheme = 0;
      }
      await this.changeTheme(this.themes[this.currentTheme]);
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.ColorService, 'Changing theme to ' + this.currentTheme);
    }
    if (theme !== undefined) {
      localStorage.setItem('app-theme', this.currentTheme.toString());
      this.isDarkTheme = this.themes[this.currentTheme] === this.darkTheme || this.themes[this.currentTheme] === this.shiftedDarkTheme;
      for (const key of Object.keys(theme)) {
        this.themeWrapper.style.setProperty(key, theme[key]);
      }
    }
  }
}
